import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

function formatDate(str) {
  if (!str) return ''
  return new Date(str).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
}
function formatCurrency(n) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0)
}

const C = {
  bg: '#f5f5f3',
  card: '#ffffff',
  border: '#e5e5e5',
  text: '#1a1a1a',
  text2: '#52525b',
  text3: '#a1a1aa',
  purple: '#7F77DD',
  purpleBg: '#EEEDFE',
  red: '#dc2626',
  redBg: '#fef2f2',
  meta: { bg: 'rgba(55,138,221,0.08)', color: '#378ADD' },
  google: { bg: 'rgba(99,153,34,0.08)', color: '#639922' },
  tiktok: { bg: '#f0f0ee', color: '#888780' },
}

const font = "system-ui, -apple-system, 'Segoe UI', sans-serif"

export default function Reports() {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [clients, setClients] = useState([])
  const [filterClient, setFilterClient] = useState('all')
  const [copied, setCopied] = useState(false)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => { loadReports(); loadClients() }, [])

  async function loadReports() {
    setLoading(true)
    const { data } = await supabase
      .from('reports').select('*, clients(name)').order('created_at', { ascending: false })
    setReports(data || [])
    setLoading(false)
  }

  async function loadClients() {
    const { data } = await supabase
      .from('clients').select('id, name').eq('active', true).order('name')
    setClients(data || [])
  }

  async function deleteReport(id) {
    if (!window.confirm('Eliminare questo report?')) return
    setDeleting(id)
    await supabase.from('reports').delete().eq('id', id)
    setReports(prev => prev.filter(r => r.id !== id))
    if (selected?.id === id) setSelected(null)
    setDeleting(null)
  }

  const filtered = filterClient === 'all' ? reports : reports.filter(r => r.client_id === filterClient)

  const isHtml = (content) => typeof content === 'string' && content.trimStart().startsWith('<')

  function handleCopy() {
    if (!selected) return
    navigator.clipboard.writeText(selected.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: font, overflow: 'hidden', background: C.bg }}>

      {/* ── Pannello sinistro ── */}
      <div style={{ width: 300, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.card }}>

        {/* Header */}
        <div style={{ padding: '20px 16px 14px', borderBottom: `1px solid ${C.border}` }}>
          <h1 style={{ fontSize: 16, fontWeight: 500, color: C.text, margin: '0 0 10px' }}>Report</h1>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px', color: C.text, fontSize: 12, fontFamily: font, outline: 'none', cursor: 'pointer' }}>
            <option value="all">Tutti i clienti ({reports.length})</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({reports.filter(r => r.client_id === c.id).length})</option>
            ))}
          </select>
        </div>

        {/* Lista report */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: C.text3, fontSize: 13 }}>Caricamento...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: C.text3, fontSize: 13 }}>Nessun report ancora.</div>
          ) : filtered.map(r => {
            const active = selected?.id === r.id
            return (
              <div key={r.id} onClick={() => setSelected(r)}
                style={{
                  background: active ? C.purpleBg : C.card,
                  border: `1px solid ${active ? C.purple : C.border}`,
                  borderRadius: 8, padding: '12px 14px', cursor: 'pointer', marginBottom: 6,
                  transition: 'all 0.12s',
                }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: active ? C.purple : C.text, marginBottom: 4, lineHeight: 1.3 }}>{r.title}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: C.text3 }}>{r.clients?.name} · {formatDate(r.created_at)}</div>
                  {r.meta?.totalSpend > 0 && (
                    <div style={{ fontSize: 12, color: active ? C.purple : C.text2, fontWeight: 500 }}>{formatCurrency(r.meta.totalSpend)}</div>
                  )}
                </div>
                {r.meta?.platforms && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    {r.meta.platforms.map(p => (
                      <span key={p} style={{
                        fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                        background: p === 'meta' ? C.meta.bg : p === 'google' ? C.google.bg : C.tiktok.bg,
                        color: p === 'meta' ? C.meta.color : p === 'google' ? C.google.color : C.tiktok.color,
                      }}>{p}</span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Pannello destro ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selected ? (
          <>
            {/* Header */}
            <div style={{ padding: '20px 24px 14px', borderBottom: `1px solid ${C.border}`, background: C.card, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 500, color: C.text, margin: '0 0 4px' }}>{selected.title}</h2>
                <div style={{ fontSize: 12, color: C.text3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <span>{formatDate(selected.date_from)} → {formatDate(selected.date_to)}</span>
                  <span style={{ color: C.border }}>·</span>
                  <span>{selected.clients?.name}</span>
                  {selected.meta?.totalSpend > 0 && <>
                    <span style={{ color: C.border }}>·</span>
                    <span>{formatCurrency(selected.meta.totalSpend)}</span>
                  </>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={handleCopy}
                  style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 14px', color: copied ? C.purple : C.text2, fontSize: 12, fontFamily: font, cursor: 'pointer', fontWeight: 500 }}>
                  {copied ? 'Copiato' : isHtml(selected.content) ? 'Copia HTML' : 'Copia testo'}
                </button>
                <button onClick={() => deleteReport(selected.id)} disabled={deleting === selected.id}
                  style={{ background: C.redBg, border: `1px solid rgba(220,38,38,0.15)`, borderRadius: 6, padding: '7px 14px', color: C.red, fontSize: 12, fontFamily: font, cursor: 'pointer', fontWeight: 500 }}>
                  {deleting === selected.id ? '...' : 'Elimina'}
                </button>
              </div>
            </div>

            {/* KPI */}
            {selected.meta?.totalSpend > 0 && (
              <div style={{ display: 'flex', gap: 10, padding: '12px 24px', borderBottom: `1px solid ${C.border}`, background: C.card }}>
                {[
                  { label: 'Spesa totale', value: formatCurrency(selected.meta.totalSpend) },
                  { label: 'Impressioni', value: (selected.meta.totalImpressions || 0).toLocaleString('it-IT') },
                  { label: 'Click', value: (selected.meta.totalClicks || 0).toLocaleString('it-IT') },
                  { label: 'Campagne', value: (selected.meta.campaigns || []).length },
                ].map(m => (
                  <div key={m.label} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 16px', flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 2 }}>{m.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{m.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Contenuto */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: C.bg }}>
              {isHtml(selected.content) ? (
                <div
                  style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '24px 28px', lineHeight: 1.7, fontSize: 14, color: C.text }}
                  dangerouslySetInnerHTML={{ __html: selected.content }}
                />
              ) : (
                <pre style={{
                  margin: 0, fontSize: 13, color: C.text2, lineHeight: 1.8,
                  fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Code', monospace",
                  whiteSpace: 'pre-wrap', background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 10, padding: '20px 24px',
                }}>
                  {selected.content}
                </pre>
              )}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text3, fontSize: 13 }}>
            Seleziona un report per visualizzarlo
          </div>
        )}
      </div>
    </div>
  )
}
